// @ts-check

/**
 * @file Renders a single auth-token list row with rename and revoke actions.
 */

import _ from "gettext-universal/build/src/translate.js"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../components/button"
import Card from "../../components/card"
import Text from "../../components/text"
import formatDate from "../../shared/format-date"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TokenRowProps
 * @property {Date | null} createdAt When the token was created.
 * @property {string} editPath Route target for renaming the token.
 * @property {string} id Auth token identifier.
 * @property {Date | null} lastUsedAt When the token last authenticated a request.
 * @property {string} name User-given token label.
 * @property {(id: string) => void} onDeletePress Revoke callback.
 * @property {string} tokenPrefix Leading characters of the token for recognition.
 */

/** @typedef {Record<string, never>} TokenRowState */

/**
 * Auth token list row.
 * @augments {ShapeComponent<TokenRowProps, TokenRowState>}
 */
class TokenRow extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the row. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the token label, metadata, and row actions.
   * @returns {ReactNode} Auth token row content.
   */
  render() {
    const {createdAt, editPath, id, lastUsedAt, name, tokenPrefix} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const lastUsedText = lastUsedAt
      ? _("Last used %{date}", {date: formatDate(lastUsedAt)})
      : _("Never used")

    return (
      <Card
        style={styles.row ||= {
          alignItems: "center",
          flexDirection: "row",
          gap: 12,
          justifyContent: "space-between",
          marginBottom: 12
        }}
        testID={`authTokenRow-${id}`}
      >
        <View
          style={styles.info ||= {
            flex: 1,
            gap: 4
          }}
          testID={`authTokenRow-${id}/info`}
        >
          <Text
            style={styles[`name-${themeName}`] ||= {
              color: colors.text,
              fontSize: 16,
              fontWeight: 600
            }}
            testID={`authTokenRow-${id}/name`}
          >
            {name}
          </Text>
          <Text
            style={styles[`prefix-${themeName}`] ||= {
              color: colors.textMuted,
              fontFamily: "monospace",
              fontSize: 14
            }}
            testID={`authTokenRow-${id}/prefix`}
          >
            {`${tokenPrefix}…`}
          </Text>
          <Text
            style={styles[`meta-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 13
            }}
            testID={`authTokenRow-${id}/meta`}
          >
            {`${_("Created %{date}", {date: formatDate(createdAt)})} · ${lastUsedText}`}
          </Text>
        </View>
        <View
          style={styles.actions ||= {
            flexDirection: "row",
            gap: 8
          }}
          testID={`authTokenRow-${id}/actions`}
        >
          <Button
            href={editPath}
            iconName="pen-to-square"
            label={_("Rename")}
            testID={`authTokenRow-${id}/editButton`}
            variant="outline"
          />
          <Button
            iconName="trash"
            label={_("Revoke")}
            onPress={this.tt.onDeletePress}
            testID={`authTokenRow-${id}/deleteButton`}
            variant="destructive-outline"
          />
        </View>
      </Card>
    )
  }

  /**
   * Forwards revoke presses with this row's token id.
   * @returns {void} Nothing.
   */
  onDeletePress = () => {
    this.p.onDeletePress(this.p.id)
  }
}

/** @type {NamedExoticComponent<TokenRowProps>} */
const TokenRowComponent = memo(shapeComponent(TokenRow))

export default TokenRowComponent
